import { PUBLIC_STUN, PLATFORM_STUN } from '@/config/targets'
import { classifyNat, uniquePorts, type NatType, type SrflxCandidate } from './candidates'
import { gatherSrflxCandidates } from './webrtc-probe'
import type { UdpStatus } from './status'

/** Which STUN servers produced the answer we are reporting. */
export type StunSource = 'platform' | 'public'

export interface UdpCheckResult {
	status: Exclude<UdpStatus, 'pending'>
	/** Null when nothing answered, so there was no mapping to classify. */
	natType: NatType | null
	/** True only when the platform's own STUN servers answered. */
	reachedSubnet: boolean
	publicIp: string | null
	mappedPorts: number[]
	via: StunSource | null
}

type Gatherer = (stunUrls: string[]) => Promise<SrflxCandidate[]>

/**
 * Two questions, asked in order. The platform's own STUN servers answer the one that matters: can UDP
 * reach the platform's subnet on 3478? Only if they stay silent do we ask public servers, and that
 * answer separates "this firewall blocks the platform's range" from "this firewall blocks UDP".
 */
export async function runUdpCheck(gather: Gatherer = gatherSrflxCandidates): Promise<UdpCheckResult> {
	const platform = await gather(PLATFORM_STUN)
	if (platform.length > 0) {
		const mappedPorts = uniquePorts(platform)
		const natType = classifyNat(mappedPorts)

		return {
			status: natType === 'symmetric' ? 'symmetric' : 'ok',
			natType,
			reachedSubnet: true,
			publicIp: platform[0].ip,
			mappedPorts,
			via: 'platform'
		}
	}

	const fallback = await gather(PUBLIC_STUN)
	if (fallback.length > 0) {
		const mappedPorts = uniquePorts(fallback)

		return {
			status: 'subnet-blocked',
			natType: classifyNat(mappedPorts),
			reachedSubnet: false,
			publicIp: fallback[0].ip,
			mappedPorts,
			via: 'public'
		}
	}

	return {
		status: 'udp-blocked',
		natType: null,
		reachedSubnet: false,
		publicIp: null,
		mappedPorts: [],
		via: null
	}
}
